import React from 'react'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import type { SelectOption } from '@ztechapps/webui.input'
import type { SpreadsheetCellSelectMode } from '@ztechapps/webui.data-tools'
import { SelectCreatable } from '@ztechapps/webui.input'
import { SPREADSHEET_CELL_MENU_PROPS, SpreadsheetCellSelectCreatable } from '@ztechapps/webui.data-tools'
import { useMetadataContext } from './Context'

export type CommonMetadataSelectProps = {
  value?: string
  setValue?: (next: any) => void
  onChange?: (selected: SelectOption) => void
  onCreateSelect?: (input: string) => void
  spreadsheetMode?: SpreadsheetCellSelectMode
}

export const CommonMetadataSelect = ({
  value,
  setValue,
  onChange,
  onCreateSelect,
  spreadsheetMode,
}: CommonMetadataSelectProps) => {
  const { metadataSchema, hasRole } = useMetadataContext()

  // Resolved during render so the selected option is an element of `options` at mount: react-select
  // highlights the selected option at mount only then, and a re-render passing a new array or value
  // object moves the highlight to the first option.
  const options = React.useMemo(
    () =>
      orderBy(
        map(
          metadataSchema,
          (schemaValue, key) => ({ value: key, label: schemaValue?.name, ...schemaValue }) as SelectOption
        ),
        ['label']
      ),
    [metadataSchema]
  )
  const selectedOption = React.useMemo(() => {
    if (isEmpty(value)) return null
    return find(options, (item) => item.value === value) ?? ({ value, label: value } as SelectOption)
  }, [options, value])

  const onMetadataSelect = (selectedMetadata: SelectOption) => {
    // Legacy callsite shape: `setValue` consumers receive an object with a `key` field. The
    // `...(value as any)` spread is a no-op when `value` is a string and a copy when consumers
    // (e.g., `AddMetadata`) pass an object — preserves the existing dual-shape contract without
    // widening the public prop type.
    const newValue = { ...(value as unknown as Record<string, any>) }
    newValue.key = selectedMetadata.value
    setValue?.(newValue)
    onChange?.(selectedMetadata)
  }

  const sharedProps = {
    disabled: !hasRole,
    value: selectedOption,
    options,
    onChange: onMetadataSelect,
    onInputChange: (input: string) => {
      onCreateSelect?.(input)
    },
    ...SPREADSHEET_CELL_MENU_PROPS,
  }

  if (spreadsheetMode) {
    return (
      <SpreadsheetCellSelectCreatable
        {...sharedProps}
        onMenuStateChange={spreadsheetMode.onMenuStateChange}
        onCommit={spreadsheetMode.onCommit}
        defaultMenuIsOpen={spreadsheetMode.defaultMenuIsOpen}
        defaultInputValue={spreadsheetMode.defaultInputValue}
      />
    )
  }

  return (
    // Prevent clicks on the dropdown from closing any parent modal or overlay.
    <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <SelectCreatable {...sharedProps} />
    </div>
  )
}
